import React from "react";
import { Link } from "react-router-dom";
import {
  Instagram,
  Mail,
  Phone,
  MapPin,
  ArrowUpRight,
  Sparkles,
} from "lucide-react";
import {
  FREEAYTO_LOGO,
  CONTACT_EMAIL,
  CONTACT_LOCATION,
  CONTACT_PHONE,
} from "../data/mock";
import NewsletterOptIn from "./NewsletterOptIn";
import LiveCounterTicker from "./LiveCounterTicker";

const TikTokIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.1z" />
  </svg>
);

const SOCIALS = [
  { Ico: Instagram, href: "https://instagram.com/freeayto", label: "Instagram" },
  { Ico: TikTokIcon, href: "https://tiktok.com/@freeayto", label: "TikTok" },
  { Ico: Mail, href: `mailto:${CONTACT_EMAIL}`, label: "E-Mail" },
];

const Footer = () => {
  const year = new Date().getFullYear();

  return (
    <footer className="relative bg-[#0a0a0a] text-neutral-400 overflow-hidden">
      {/* Cinematic ambient glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[1100px] h-[700px] rounded-full bg-[radial-gradient(ellipse,rgba(236,72,153,0.08),rgba(249,115,22,0.05)_30%,rgba(168,85,247,0.04)_55%,transparent_75%)]" />
        <div className="absolute bottom-0 right-0 w-[600px] h-[400px] rounded-full bg-[radial-gradient(ellipse,rgba(168,85,247,0.06),transparent_70%)]" />
      </div>

      {/* Top gradient hairline */}
      <div className="relative h-px bg-gradient-to-r from-transparent via-pink-500/40 to-transparent" />

      <div className="relative max-w-[1300px] mx-auto px-6 md:px-10 pt-24 pb-10">
        {/* Live counter ticker */}
        <div className="mb-14">
          <LiveCounterTicker />
        </div>

        {/* CTA banner */}
        <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.04] via-white/[0.02] to-transparent backdrop-blur-sm p-8 md:p-14 mb-20">
          <div className="absolute -top-32 -right-20 w-[500px] h-[500px] rounded-full bg-[radial-gradient(circle,rgba(236,72,153,0.18),rgba(249,115,22,0.08)_40%,transparent_70%)] pointer-events-none" />
          <div className="relative flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-[10px] uppercase tracking-[0.25em] font-bold text-neutral-300">
                <Sparkles size={11} className="text-pink-400" />
                Bereit für mehr Reichweite?
              </div>
              <h3 className="mt-5 font-display font-extrabold text-3xl md:text-5xl lg:text-6xl tracking-[-0.03em] leading-[1.05] text-white">
                Lass uns Content bauen,{" "}
                <span className="bg-gradient-to-r from-pink-400 via-orange-400 to-purple-400 bg-clip-text text-transparent">
                  der verkauft.
                </span>
              </h3>
              <p className="mt-5 text-neutral-400 leading-relaxed max-w-xl">
                Kostenloses Erstgespräch, individuelle Analyse, ehrliche Empfehlung – ohne Verkaufs-Show.
              </p>
            </div>
            <Link
              to="/kontakt"
              className="group inline-flex items-center justify-center gap-3 px-8 py-5 rounded-full bg-white text-neutral-950 text-sm font-bold tracking-[0.12em] uppercase hover:bg-gradient-to-r hover:from-pink-400 hover:via-orange-400 hover:to-purple-400 hover:text-white transition-all duration-500 shadow-[0_20px_60px_-20px_rgba(255,255,255,0.4)] hover:shadow-[0_24px_60px_-15px_rgba(236,72,153,0.6)] self-start lg:self-end shrink-0"
              data-testid="footer-cta-kontakt"
            >
              Projekt anfragen
              <ArrowUpRight
                size={18}
                className="transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              />
            </Link>
          </div>
        </div>

        {/* Main grid */}
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 pb-16">
          {/* Brand block + Newsletter */}
          <div className="lg:col-span-5 space-y-10">
            <div>
              <Link to="/" className="inline-block">
                <img
                  src={FREEAYTO_LOGO}
                  alt="FREEAYTO"
                  className="h-11 w-auto"
                />
              </Link>
              <p className="mt-6 text-neutral-400 max-w-md leading-relaxed text-[15px]">
                Social Media mit Struktur. Content mit Wirkung. Wir machen aus
                Reichweite Ergebnisse – für Premium-Brands, Gastronomie und Stars.
              </p>

              <div className="mt-8 flex items-center gap-2">
                {SOCIALS.map(({ Ico, href, label }) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={label}
                    className="group w-11 h-11 rounded-full bg-white/[0.04] border border-white/10 hover:border-white/30 flex items-center justify-center transition-all duration-300 hover:-translate-y-0.5"
                  >
                    <Ico size={15} className="text-neutral-300 group-hover:text-white transition-colors" />
                  </a>
                ))}
              </div>
            </div>

            <NewsletterOptIn source="footer" />
          </div>

          {/* Navigation columns */}
          <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-8">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-neutral-500">
                Navigation
              </div>
              <ul className="mt-5 space-y-3 text-sm">
                {[
                  { l: "Home", to: "/" },
                  { l: "Leistungen", to: "/leistungen" },
                  { l: "Erfolge", to: "/erfolge" },
                  { l: "Team", to: "/team" },
                  { l: "Kontakt", to: "/kontakt" },
                ].map((i) => (
                  <li key={i.to}>
                    <Link
                      to={i.to}
                      className="text-neutral-300 hover:text-white transition-colors inline-flex items-center gap-1.5 group"
                    >
                      {i.l}
                      <ArrowUpRight
                        size={12}
                        className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300 text-pink-400"
                      />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-neutral-500">
                Free Tools
              </div>
              <ul className="mt-5 space-y-3 text-sm">
                <li>
                  <Link
                    to="/tools/video-skript"
                    className="text-neutral-300 hover:text-white transition-colors inline-flex items-center gap-1.5 group"
                  >
                    Video-Skript Ersteller
                    <ArrowUpRight
                      size={12}
                      className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300 text-pink-400"
                    />
                  </Link>
                </li>
                <li>
                  <Link
                    to="/tools/social-media-check"
                    className="text-neutral-300 hover:text-white transition-colors inline-flex items-center gap-1.5 group"
                  >
                    Social Media Check
                    <ArrowUpRight
                      size={12}
                      className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300 text-pink-400"
                    />
                  </Link>
                </li>
              </ul>
            </div>

            <div className="col-span-2 sm:col-span-1">
              <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-neutral-500">
                Kontakt
              </div>
              <ul className="mt-5 space-y-3 text-sm">
                <li>
                  <a
                    href={`mailto:${CONTACT_EMAIL}`}
                    className="text-neutral-300 hover:text-white transition-colors inline-flex items-center gap-2"
                  >
                    <Mail size={12} className="text-pink-400" />
                    {CONTACT_EMAIL}
                  </a>
                </li>
                <li>
                  <a
                    href={`tel:${CONTACT_PHONE.replace(/\s/g, "")}`}
                    className="text-neutral-300 hover:text-white transition-colors inline-flex items-center gap-2"
                  >
                    <Phone size={12} className="text-orange-400" />
                    {CONTACT_PHONE}
                  </a>
                </li>
                <li className="text-neutral-400 inline-flex items-center gap-2">
                  <MapPin size={12} className="text-purple-400" />
                  {CONTACT_LOCATION}
                </li>
                <li className="text-neutral-500 text-xs pt-1">Mo – Fr · 9 – 18 Uhr</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Thin divider */}
        <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />

        {/* Bottom bar */}
        <div className="pt-8 flex flex-col-reverse md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-x-6 gap-y-2 text-xs text-neutral-500">
            <div>© {year} FREEAYTO Media. Alle Rechte vorbehalten.</div>
            <div className="hidden sm:block w-px h-3 bg-white/10" />
            <div className="flex items-center gap-5">
              <Link to="/impressum" className="hover:text-white transition-colors">
                Impressum
              </Link>
              <Link to="/datenschutz" className="hover:text-white transition-colors">
                Datenschutz
              </Link>
            </div>
          </div>

          {/* Tech partner label – contextualizes the floating badge bottom-right */}
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-semibold text-neutral-600 md:pr-[200px]">
            <span className="w-6 h-px bg-white/15" />
            Tech Partner
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
